import { useQuery, useQueryClient } from "@tanstack/react-query";
import { custom, z } from "zod";
import { Decimal } from "decimal.js";
import {
    fetchApiGet,
    fetchApiPut,
    fetchApiPost,
    fetchApiDelete,
} from "../../utils";
import { BaseDbObject } from "@/lib/models/db";

export interface ClientPosition extends BaseDbObject {
    is_manually_inserted?: boolean;
    trading_account: string;

    customer_id?: string;
    trader_id?: string;
    trading_account_id: string;

    enabled: boolean;
    commodity?: string;
    crop_year: number;
    contract?: string;
    option_month?: string;

    expiry_date?: Date;
    note?: string;
    source_type?: "rjo" | "Open Orders" | "OTC" | "Manual";
    original_row?: any;

    net_position: string;
    long_qty: number;
    short_qty: number;

    bushel?: Decimal;
    put_or_call?: string;
    strike_price?: Decimal;
    price: Decimal;
    premium: Decimal;

    instrument_type?: string;

    Customer?: { long_name: string; id: string }
}

export const PositionsFiltersValidation = z.object({
    enabled: z.boolean().optional(),
    customer_ids: z.array(z.string().uuid()).optional(),
    trader_ids: z.array(z.string().uuid()).optional(),
    crop_year: z.coerce.number().optional(),
});

export interface ListPositionsResult {
    positions: ClientPosition[]
    open_offers: any[];
    otc_offer_ups: any[];
}

export function useListRiskPositions({ filters, enabled }: { filters?: z.infer<typeof PositionsFiltersValidation>; enabled?: boolean }) {
    const queryParams = new URLSearchParams();

    if (filters) {
        if (filters.customer_ids?.length! > 0) {
            queryParams.set("customer_ids", JSON.stringify((filters.customer_ids)));
        }
        if (filters.trader_ids?.length! > 0) {
            queryParams.set("trader_ids", JSON.stringify((filters.trader_ids)));
        }
        if (filters.enabled !== undefined) {
            queryParams.set("enabled", filters.enabled.toString());
        }
        if (filters.crop_year != null) {
            queryParams.set("crop_year", filters.crop_year?.toString());
        }
    }

    return useQuery({
        queryKey: ["risk", "client_positions", "top_level", queryParams.toString()],
        queryFn: () =>
            fetchApiGet<ListPositionsResult>("risk/positions", queryParams),
        retry: 2,
        keepPreviousData: true,
        enabled,
    });
}
